import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
  colors,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  ChallengeEventTypes,
  MatchEventType,
} from "../../directus/collections";
import { MatchFilter } from "../../libs/statsFilters";
import { webWorkerQuery } from "../../libs/workers/webWorker";
import { useChallengeMatchOverviewDialog } from "../../providers/ChallengeMatchOverviewDialogProvider"; // ✅ Import dialog provider
import { fromHexToHsl } from "../../utils/colors";
import { WeeklyHeatMap } from "../WeeklyHeatMap/WeeklyHeatMap";
import { ColorConfig } from "../WeeklyHeatMap/WeeklyHeatMap.types";
import {
  DisplayFormat,
  EventsWeeklyHeatMapWorkerInput,
  EventsWeeklyHeatMapWorkerOutput,
} from "./configs";
import EventsWeeklyHeatMapWorker from "./EventsWeeklyHeatMap.worker.ts?worker";
import { WorkerQueryTransferModes } from "../../libs/workers/consts";

async function processData(
  input: EventsWeeklyHeatMapWorkerInput
): Promise<EventsWeeklyHeatMapWorkerOutput | null> {
  const worker = new EventsWeeklyHeatMapWorker();
  return webWorkerQuery(worker, {
    type: "CALCULATE_HEATMAP",
    data: input,
    opts: {
      traceKey: `EventsWeeklyHeatMapWorker-${Date.now()}`,
      response: {
        transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
      },
    },
  })
    .then((res) => {
      console.log("Terminating EventsWeeklyHeatMapWorker");
      return res;
    })
    .finally(() => {
      worker.terminate();
    });
}

function useEventsWeeklyHeatMapData(
  cityEscapeId?: string | null,
  enigmaId?: string,
  filters?: MatchFilter,
  selectedEventType: MatchEventType = "challenge_init",
  trimEmptyHours: boolean = true,
  displayFormat: DisplayFormat = DisplayFormat.PERCENTAGE_TOTAL
) {
  return useQuery({
    queryKey: [
      "EventsWeeklyHeatMapData",
      cityEscapeId,
      enigmaId,
      filters,
      selectedEventType,
      trimEmptyHours,
      displayFormat,
    ],
    queryFn: () =>
      processData({
        cityEscapeId: cityEscapeId || undefined,
        enigmaId,
        filters,
        selectedEventType,
        trimEmptyHours,
        displayFormat,
      }),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: cityEscapeId !== undefined,
  });
}

interface EventsWeeklyHeatMapProps {
  cityEscapeId?: string | null;
  enigmaId?: string;
  loading?: boolean;
  trimEmptyHours?: boolean; // Nasconde ore esterne al range min-max
  displayFormat?: DisplayFormat; // Formato di visualizzazione
  filters?: MatchFilter; // Filtro per la ricerca
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  challenge_init: "Avvio",
  challenge_hint: "Ispirazione",
  challenge_requested_solution: "Resa",
  challenge_solution: "Successo",
};

// Event type colors following ChallengeTimelinesHeatMap logic
const EVENT_TYPE_COLORS: Record<
  string,
  {
    hex: string;
    hsl: [number, number, number];
    needContrastOnHighIntensity: boolean;
  }
> = {
  challenge_init: {
    hex: colors.blue[400],
    hsl: fromHexToHsl(colors.blue[400]),
    needContrastOnHighIntensity: true,
  },
  challenge_hint: {
    hex: colors.yellow[500],
    hsl: fromHexToHsl(colors.yellow[500]),
    needContrastOnHighIntensity: false,
  },
  challenge_requested_solution: {
    hex: colors.red[400],
    hsl: fromHexToHsl(colors.red[400]),
    needContrastOnHighIntensity: true,
  },
  challenge_solution: {
    hex: colors.green[400],
    hsl: fromHexToHsl(colors.green[400]),
    needContrastOnHighIntensity: true,
  },
};

const DAYS_OF_WEEK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function EventsWeeklyHeatMap({
  cityEscapeId,
  enigmaId,
  filters,
  loading,
  trimEmptyHours = true,
  displayFormat = DisplayFormat.PERCENTAGE_TOTAL,
}: EventsWeeklyHeatMapProps) {
  const theme = useTheme();
  const dialog = useChallengeMatchOverviewDialog("EventsWeeklyHeatMap"); // ✅ Hook per dialog
  const [selectedEventType, setSelectedEventType] =
    useState<MatchEventType>("challenge_init");
  const [showValues, setShowValues] = useState(true);

  // ✅ Handler per context menu
  const handleCellContextMenu = (
    eventDetails: Array<{ matchId: string; targetId: string }>,
    description: string
  ) => {
    console.log("Context menu clicked:", { eventDetails, description });

    const dialogData = eventDetails.map(({ matchId, targetId }) => ({
      matchId,
      targetId,
    }));

    dialog.openModal({
      title: `Dettagli Eventi - ${description}`,
      loading: false,
      data: dialogData,
    });
  };

  const chartData = useEventsWeeklyHeatMapData(
    cityEscapeId,
    enigmaId,
    filters,
    selectedEventType,
    trimEmptyHours,
    displayFormat
  );

  // Extract computed values from worker data
  const {
    heatmapData,
    maxCount,
    activeHours,
    totalMatches,
    dayTotals,
    hourTotals,
    maxPosition,
    totalEvents,
  } = useMemo(() => {
    if (!chartData.data) {
      // Default empty state
      return {
        heatmapData: Array(7)
          .fill(null)
          .map(() =>
            Array(24).fill({
              value: 0,
              displayValue: "",
              tooltipContent: "",
              isHighlighted: false,
            })
          ),
        maxCount: 0,
        activeHours: HOURS,
        totalMatches: 0,
        dayTotals: Array(7).fill(0),
        hourTotals: Array(24).fill(0),
        maxPosition: null,
        totalEvents: 0,
      };
    }

    return chartData.data;
  }, [chartData.data]);

  // Custom color intensity function for percentage displays
  const getColorIntensity = (value: number, maxValue: number): number => {
    if (displayFormat === DisplayFormat.PERCENTAGE_TOTAL) {
      // Use absolute percentage thresholds
      const actualPercentage =
        totalEvents > 0 ? (value / totalEvents) * 100 : 0;
      return Math.min(actualPercentage / 100, 1); // Normalize to 0-1, cap at 100%
    } else if (displayFormat === DisplayFormat.PERCENTAGE_PARTIAL) {
      // For partial percentage, use relative to total events of this type
      return maxValue > 0 ? value / maxValue : 0;
    } else {
      // For absolute values, use relative to max
      return maxValue > 0 ? value / maxValue : 0;
    }
  };

  // Create color configuration for the selected event type
  const colorConfig: ColorConfig = useMemo(() => {
    if (!EVENT_TYPE_COLORS) {
      throw new Error(`Unknown event type: ${selectedEventType}`);
    }
    const eventColorConfig =
      EVENT_TYPE_COLORS[selectedEventType] || EVENT_TYPE_COLORS.challenge_init;
    return {
      baseColor: eventColorConfig.hsl,
      emptyColor: "#f8f9fa",
      needsContrastOnHighIntensity:
        eventColorConfig.needContrastOnHighIntensity,
    };
  }, [selectedEventType]);

  // Create legend configuration
  const legendConfig = useMemo(
    () => ({
      show: true,
      title: `Colore: ${EVENT_TYPE_LABELS[selectedEventType]}`,
      maxLabel: `Max: ${maxCount} eventi`,
      intensitySteps: [
        { intensity: 0, label: "0%" },
        { intensity: 0.1, label: "5-15%" },
        { intensity: 0.2, label: "15-25%" },
        { intensity: 0.325, label: "25-40%" },
        { intensity: 0.5, label: "40-60%" },
        { intensity: 0.7, label: "60-80%" },
        { intensity: 0.9, label: "80%+" },
      ],
    }),
    [selectedEventType, maxCount]
  );

  if (!chartData.data && !chartData.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={400}
      >
        <Typography>Nessun dato disponibile</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Tipo di Evento</InputLabel>
            <Select
              value={selectedEventType}
              label="Tipo di Evento"
              onChange={(e) =>
                setSelectedEventType(e.target.value as MatchEventType)
              }
            >
              {ChallengeEventTypes.map((eventType) => (
                <MenuItem key={eventType} value={eventType}>
                  {EVENT_TYPE_LABELS[eventType]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={showValues}
                onChange={(e) => setShowValues(e.target.checked)}
                size="small"
              />
            }
            label="Mostra valori"
          />
        </Box>
      </Box>

      {chartData.error && (
        <Box mb={2} p={2} bgcolor={theme.palette.error.light} borderRadius={1}>
          <Typography color="error" variant="body2">
            Errore nel calcolo della heatmap: {chartData.error.message}
          </Typography>
        </Box>
      )}

      <WeeklyHeatMap
        data={heatmapData}
        displayHours={activeHours}
        colorConfig={colorConfig}
        getColorIntensity={getColorIntensity}
        showValues={showValues}
        loading={loading || chartData.isLoading}
        emptyMessage="Nessun dato disponibile"
        dayLabels={DAYS_OF_WEEK}
        maxValue={maxCount}
        dayTotals={dayTotals}
        hourTotals={hourTotals}
        totalEvents={totalEvents}
        onCellContextMenu={handleCellContextMenu} // ✅ Passa callback
        legend={legendConfig}
      />

      {/* Informazioni aggiuntive */}
      {!chartData.isLoading && chartData.data && (
        <Box mt={2} display="flex" flexWrap="wrap" gap={3} alignItems="center">
          {maxPosition && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.error.main, fontWeight: "bold" }}
            >
              ⭐ Picco: {DAYS_OF_WEEK[maxPosition.day]}{" "}
              {maxPosition.hour.toString().padStart(2, "0")}:00 ({maxCount}{" "}
              eventi)
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            📊 Totale eventi "{EVENT_TYPE_LABELS[selectedEventType]}":{" "}
            <strong>{totalEvents?.toLocaleString()}</strong>
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            🎯 Su {totalMatches?.toLocaleString()} partite totali
            {totalMatches > 0 && (
              <span>
                ({((totalEvents / totalMatches) * 100).toFixed(1)}% copertura)
              </span>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
